import { useDispatch } from "react-redux";
import { JhUseToast } from "../Tailwind/components/design-packages/toastPackage";
import { resetSnackbarMessage } from "../components/Snackbar/snackbarActions";

export type ToastVariant = "default" | "destructive" | "success";

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
}

export interface UseToastReturn {
  showToast: (options: ToastOptions) => void;
  showSuccess: (title: string, description?: string) => void;
  showError: (title: string, description?: string) => void;
  showWarning: (title: string, description?: string) => void;
  showInfo: (title: string, description?: string) => void;
}

/**
 * Custom hook for showing toast notifications with predefined variants
 * Provides a consistent API for different types of notifications
 * Resets Redux snackbar messages to prevent duplicates in Snackbar
 */
export const useToast = (): UseToastReturn => {
  const { toast } = JhUseToast();
  const dispatch = useDispatch();

  const showToast = (options: ToastOptions) => {
    toast({
      title: options.title,
      description: options.description,
      variant: options.variant || "default"
    });
    // Reset Redux snackbar to prevent showing in Snackbar
    dispatch(resetSnackbarMessage());
  };

  const showSuccess = (title: string, description?: string) => {
    showToast({
      title,
      description,
      variant: "success"
    });
  };

  const showError = (title: string, description?: string) => {
    showToast({
      title,
      description,
      variant: "destructive"
    });
  };

  const showWarning = (title: string, description?: string) => {
    showToast({
      title,
      description,
      variant: "default" // Using default for warning as warning variant is not available
    });
  };

  const showInfo = (title: string, description?: string) => {
    showToast({
      title,
      description,
      variant: "default"
    });
  };

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};
